import { DownloadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOnlineManager } from '~/hooks/use-online-manager';
import { exportToCsv, exportToPdf } from '~/lib/export';
import { fetchAllRows } from '~/modules/common/data-table/fetch-all-rows';
import type { ColumnOrColumnGroup } from '~/modules/common/data-table/types';
import { toaster } from '~/modules/common/toaster/toaster';
import { TooltipButton } from '~/modules/common/tooltip-button';
import { Button } from '~/modules/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/modules/ui/dropdown-menu';
import { uiStore } from '~/modules/ui/ui-store';
import { getRouter } from '~/routes/-router-instance';

interface Props<TData> {
  filename: string;
  columns: ColumnOrColumnGroup<TData>[];
  selectedRows?: TData[];
  fetchRows: (limit: number, offset: number) => Promise<TData[]>;
  className?: string;
}

// biome-ignore lint/suspicious/noExplicitAny: any is required here
export function Export<R extends Record<string, any>>({ filename, columns, selectedRows, fetchRows, className = '' }: Props<R>) {
  const { t } = useTranslation();
  const isOnline = useOnlineManager();
  const mode = uiStore.getState().mode;

  const exportDefault = async (type: 'csv' | 'pdf') => {
    if (!isOnline) return toaster.warning(t('c:action.offline.text'));
    const rows = await fetchAllRows(fetchRows);
    const filenameWithExtension = `${filename}.${type}`;

    if (type === 'csv') return exportToCsv(columns, rows, filenameWithExtension);
    return exportToPdf(columns, rows, filenameWithExtension, getRouter().state.location.pathname, mode);
  };

  const exportSelected = async (type: 'csv' | 'pdf') => {
    if (!selectedRows) return toaster.warning(t('error:no_selected_rows'));
    const filenameWithExtension = `${filename}.${type}`;

    if (type === 'csv') return exportToCsv(columns, selectedRows, filenameWithExtension);

    if (!isOnline) toaster.warning(t('c:action.offline.text'));
    return exportToPdf(columns, selectedRows, filenameWithExtension, getRouter().state.location.pathname, mode);
  };

  return (
    <DropdownMenu>
      <TooltipButton className={className} toolTipContent={t('c:export_pdf_csv')}>
        <DropdownMenuTrigger render={<Button variant="outline" className="flex max-xs:hidden" />}>
          <DownloadIcon />
          <span className="ml-1 max-xl:hidden">{t('c:export')}</span>
        </DropdownMenuTrigger>
      </TooltipButton>
      <DropdownMenuContent align="end" className="p-1">
        {isOnline && (
          <>
            {/* Label the full-export pair so it reads apart from the selected-rows pair below */}
            <DropdownMenuItem onClick={() => exportDefault('csv')}>
              <span>CSV</span>
              <span className="ml-2 text-muted-foreground text-xs">{t('c:all_rows').toLowerCase()}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportDefault('pdf')}>
              <span>PDF</span>
              <span className="ml-2 text-muted-foreground text-xs">{t('c:all_rows').toLowerCase()}</span>
            </DropdownMenuItem>
          </>
        )}
        {selectedRows && (
          <>
            <DropdownMenuItem onClick={() => exportSelected('csv')} disabled={selectedRows.length === 0}>
              <span>CSV</span>
              <span className="ml-2 text-muted-foreground text-xs">
                {selectedRows.length ? `${selectedRows.length} ${t('c:selected').toLowerCase()}` : t('c:no_selection').toLowerCase()}
              </span>
            </DropdownMenuItem>

            {isOnline && (
              <DropdownMenuItem onClick={() => exportSelected('pdf')} disabled={selectedRows.length === 0}>
                <span>PDF</span>
                <span className="ml-2 text-muted-foreground text-xs">
                  {selectedRows.length ? `${selectedRows.length} ${t('c:selected').toLowerCase()}` : t('c:no_selection').toLowerCase()}
                </span>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
